import { useEffect, useMemo, useState } from 'react';
import { Container } from './Container';
import { Button } from './Button';
import { PlayExampleModal } from './PlayExampleModal';
import { useScrollReveal } from './useScrollReveal';
import { getStaticLandingGames, loadLandingGames } from './landingGames';
import { getLandingSamplePlayUrl } from './demoGame';

const staticFallbackGames = getStaticLandingGames();

function GameCard({ game, openInNewTab, onOpenModal }) {
  const href = getLandingSamplePlayUrl(game);
  const content = (
    <>
      <img
        src={game.imageUrl}
        alt={game.name || game.gameTypeBadge}
        className="sf-game-format-card-image"
        loading="lazy"
      />
      {game.tag && (
        <span className="sf-game-format-card-tag">
          {game.tag}
        </span>
      )}

      <div className="sf-game-format-card-copy">
        <h3>{game.name || game.gameTypeBadge}</h3>
        {game.description && (
          <p>{game.description}</p>
        )}
      </div>
    </>
  );

  if (!href) {
    return <article className="sf-game-format-card">{content}</article>;
  }

  const label = `Play ${game.name || game.gameTypeBadge}`;

  if (!openInNewTab) {
    return (
      <button
        type="button"
        className="sf-game-format-card"
        onClick={() => onOpenModal({ playUrl: href, title: label })}
        aria-label={label}
      >
        {content}
      </button>
    );
  }

  return (
    <a
      className="sf-game-format-card"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
    >
      {content}
    </a>
  );
}

export function GameShowcase() {
  const [revealRef, isVisible] = useScrollReveal();
  const [templateGames, setTemplateGames] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const [modalGame, setModalGame] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(pointer: coarse), (max-width: 767px)');
    const update = () => setOpenInNewTab(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    let isMounted = true;

    loadLandingGames()
      .then((games) => {
        if (!isMounted) return;
        setTemplateGames(games);
      })
      .finally(() => {
        if (isMounted) setLoadingTemplates(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const games = useMemo(() => (
    templateGames.length > 0 ? templateGames : staticFallbackGames
  ), [templateGames]);

  return (
    <section
      ref={revealRef}
      id="games"
      aria-labelledby="games-heading"
      className={`sf-section sf-section-showcase py-10 sm:py-16 landing-reveal ${isVisible ? 'visible' : ''}`}
    >
      <Container>
        <div className="mx-auto mb-8 max-w-4xl text-center sm:mb-10">
          <h2 id="games-heading" className="text-3xl font-arcade tracking-tight text-gch-accent sm:text-4xl">
            9 game formats, fully brandable
          </h2>
          <p className="mt-3 text-base leading-7 text-landing-text-secondary sm:text-lg sm:leading-8">
            Start from an arcade or quiz format, then swap in your brand, assets, questions, CTA, and campaign link.
          </p>
        </div>

        {loadingTemplates ? (
          <div className="sf-game-formats-grid">
            {Array.from({ length: 9 }).map((_, index) => (
              <div key={index} className="sf-game-format-card animate-pulse bg-white/5" />
            ))}
          </div>
        ) : (
          <div className={`sf-game-formats-grid landing-reveal-stagger ${isVisible ? 'visible' : ''}`}>
            {games.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                openInNewTab={openInNewTab}
                onOpenModal={setModalGame}
              />
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <Button href="/studio/register">Start building your game</Button>
        </div>
      </Container>
      <PlayExampleModal
        isOpen={!!modalGame}
        onClose={() => setModalGame(null)}
        playUrl={modalGame?.playUrl}
        title={modalGame?.title}
      />
    </section>
  );
}
